import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@pandiafi/design-system'
import { DetailRow } from '@pandiafi/components'

import {
  AddTenantInput,
  Contract,
  ResidentAppLocale,
  UserMetadata,
} from '../../../../common/validations'
import i18n from '../../../i18n/i18n'
import { Section } from '../../../components'
import { useAPIExecuteModificationAddTenant } from '../../../api/apiHooks/useAPIExecuteModificationAddTenant'

import { CardTitleBand } from 'components/CardTitleBand/CardTitleBand'

type Props = {
  formValues: AddTenantInput
  contract: Contract
  cancel: () => void
  onSuccess: () => void
  userMetadata: UserMetadata
}

export const AddTenantPreview: FC<Props> = ({
  formValues,
  contract,
  cancel,
  onSuccess,
  userMetadata: { isImpersonator },
}) => {
  const { t } = useTranslation(['common', 'contractModification'])

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIExecuteModificationAddTenant(contract.uuid, lang)

  const handleCancel = () => {
    cancel()
  }

  const handleSubmit = () => {
    mutate({
      operationData: formValues,
    })
  }

  useEffect(() => {
    if (isSuccess && data && data.success) {
      onSuccess()
    }
  })

  // We want to show the loading spinner until the user is redirected
  const isLoading = isPending || isSuccess

  return (
    <>
      <CardTitleBand>
        {t('contractModification:headings.modificationPreview')}
      </CardTitleBand>
      <Section className="mb-12">
        <DetailRow>
          <DetailRow.Item title={t('contractModification:labels.change')}>
            {t('contractModification:labels.personAdded')}
          </DetailRow.Item>
          {formValues.tenants.map((tenant) => (
            <DetailRow.Item
              key={tenant.socialSecurityNumber}
              title={t('contractModification:labels.personInformation')}
            >
              <span>{tenant.socialSecurityNumber}</span>
              <span>
                {tenant.firstName} {tenant.lastName}
              </span>
              <span>{tenant.email}</span>
              <span>{tenant.phoneNumber}</span>
            </DetailRow.Item>
          ))}
        </DetailRow>
      </Section>
      <div className="min-tablet:mt-16 min-tablet:flex min-tablet:items-center min-tablet:justify-end flex gap-4">
        <Button type="button" variant="cancel" onClick={() => handleCancel()}>
          {t('contractModification:buttons.backToEdit')}
        </Button>
        <Button
          type="button"
          variant="delete"
          onClick={() => handleSubmit()}
          disabled={isImpersonator || isLoading}
          loading={isLoading}
        >
          {t('contractModification:buttons.confirmModification')}
        </Button>
      </div>
    </>
  )
}
